import { Dialog, DialogContent } from "@punchup/ui-common/shadcn/ui/dialog";
import { Drawer, DrawerContent } from "@punchup/ui-common/shadcn/ui/drawer";
import { FeedEvents, trackEvent } from "@punchup/analytics";
import { cn } from "@punchup/utils/cn";
import { getWebAppHostname } from "@punchup/utils/hostname";
import { usePathname, useRouter } from "next/navigation";

import { Button } from "@punchup/ui-common/shadcn/ui/button";
import { Check } from "lucide-react";
import { CheckoutModalOrDrawer } from "@punchup/ui-common/components/checkout";
import { Comedian } from "@punchup/data/legacy/comedian";
import { ComedianAvatar } from "./ComedianAvatar";
import { ComedianName } from "./ComedianName";
import { Database } from "@punchup/data/supabase.types";
import { FOLLOW_LOCATIONS } from "@punchup/data/legacy/follows";
import Link from "next/link";
import { Loader } from "@punchup/ui-common/components/common/icons";
import { PunchupVerifiedBadge } from "@punchup/ui-common/components/common";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { followComedianById } from "@punchup/data/client";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { logException } from "@punchup/sentry";
import { toast } from "@punchup/ui-common/shadcn/ui/use-toast";
import { useMediaQuery } from "@punchup/utils/hooks";
import { useSession } from "@punchup/data";
import { useState } from "react";

interface MultipleComediansDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  comedians: (Comedian & { is_punchup_verified: boolean })[];
  followedComedians: string[];
}

export default function MultipleComediansDrawer({
  isOpen,
  setIsOpen,
  comedians,
  followedComedians,
}: MultipleComediansDrawerProps) {
  const [comedianToFollow, setComedianToFollow] = useState<
    (Comedian & { is_punchup_verified: boolean }) | null
  >(null);
  const [displaySignInModal, setDisplaySignInModal] = useState(false);
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
  const [hasFollowedComedian, setHasFollowedComedian] = useState<
    Record<string, boolean>
  >(() => {
    // Initialize with followed comedians from props
    const initialState: Record<string, boolean> = {};
    followedComedians.forEach((id) => {
      initialState[id] = true;
    });
    return initialState;
  });
  const session = useSession();
  const supabase = createClientComponentClient<Database>();
  const router = useRouter();
  const pathname = usePathname();

  const isDesktop = useMediaQuery("(min-width: 768px)");

  const handleSubscribe = async (
    comedian: Comedian & { is_punchup_verified: boolean }
  ) => {
    if (!comedian.is_punchup_verified) return;

    // If user is not signed in, show sign in modal
    if (!session?.user) {
      setComedianToFollow(comedian);
      setDisplaySignInModal(true);
      return;
    }

    // Else, follow the comedian
    // Analytics tracking is handled server-side in the v2 API
    setIsLoading((prev) => ({ ...prev, [comedian.id]: true }));
    try {
      const result = await followComedianById(
        comedian.id,
        FOLLOW_LOCATIONS.VENUE_PAGE
      );
      toast({
        title: `Successfully subscribed to ${comedian.display_name}`,
        variant: "success",
      });
      if (result.success) {
        setHasFollowedComedian((prev) => ({
          ...prev,
          [comedian.id]: true,
        }));
      }
      router.refresh();
    } catch (err) {
      logException(err);
    } finally {
      setIsLoading((prev) => ({ ...prev, [comedian.id]: false }));
    }
  };

  const sortComedians = (a: Comedian, b: Comedian) => {
    // prioritize in order of importance:
    // 1. the user's followed comedians
    // 2. comedians verified on Punchup (is_punchup_verified not false)
    // 3. comedians with images (about_img not null)
    const getScore = (comedian: Comedian) => {
      let score = 0;
      if (followedComedians?.includes(comedian.id)) {
        score += 3;
      }
      if (comedian.is_punchup_verified) {
        score += 2;
      }
      if (comedian.about_img) {
        score += 1;
      }
      return score;
    };

    const scoreA = getScore(a);
    const scoreB = getScore(b);

    return scoreB - scoreA;
  };

  // Common content for both dialog and drawer
  const comediansList = (
    <>
      <h1 className="text-lg font-semibold font-sans normal-case text-center pt-4 md:pt-0 pb-2 md:pb-0">
        Comedians
      </h1>
      <div className="space-y-1.5 pt-2 md:pt-0">
        {comedians.sort(sortComedians).map((comedian) => (
          <div
            key={comedian.id}
            className="flex items-center justify-between py-2"
          >
            {comedian.is_punchup_verified ? (
              // render a link to the comedian's page if they're verified
              <Link
                href={`${getWebAppHostname()}/${comedian.slug}`}
                className="flex items-center gap-3 md:gap-4 hover:underline underline-offset-2 cursor-pointer"
              >
                <AvatarAndName comedian={comedian} />
              </Link>
            ) : (
              <div className="flex items-center gap-3 md:gap-4">
                <AvatarAndName comedian={comedian} />
              </div>
            )}
            {comedian.is_punchup_verified && (
              <Button
                variant="punchup"
                size="sm"
                onClick={() => handleSubscribe(comedian)}
                disabled={
                  isLoading[comedian.id] || hasFollowedComedian[comedian.id]
                }
                className={cn(
                  "px-3 py-1.5 text-xs md:text-sm h-[unset] leading-none text-white font-semibold disabled:opacity-100",
                  hasFollowedComedian[comedian.id]
                    ? "bg-secondary-blue hover:bg-secondary-blue border border-secondary-blue "
                    : "bg-primary hover:bg-primary border border-primary"
                )}
              >
                {isLoading[comedian.id] ? (
                  <span className="flex items-center gap-x-2 w-16 justify-center">
                    <Loader height={6} width={6} />
                  </span>
                ) : hasFollowedComedian[comedian.id] ? (
                  <div className="flex items-center gap-x-2">
                    <Check width={17} height={17} strokeWidth={4} />
                    <span>Subscribed</span>
                  </div>
                ) : (
                  "Subscribe"
                )}
              </Button>
            )}
          </div>
        ))}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile: Drawer */}
      {!isDesktop && (
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerContent className="z-[99] font-regular p-4 md:p-6 max-h-[90vh]">
            <div className="overflow-y-auto">{comediansList}</div>
          </DrawerContent>
        </Drawer>
      )}
      {/* Desktop: Dialog */}
      {isDesktop && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="z-[50] font-regular md:max-w-md max-h-[90vh] overflow-y-auto">
            {comediansList}
          </DialogContent>
        </Dialog>
      )}

      {/* Sign In Modal */}
      {comedianToFollow && (
        <CheckoutModalOrDrawer
          isOpen={displaySignInModal}
          setIsOpen={setDisplaySignInModal}
          comedianToFollow={comedianToFollow}
          redirectTo={pathname}
          followLocation={FOLLOW_LOCATIONS.VENUE_PAGE}
          onOpen={(params) => {
            trackEvent(FeedEvents.OPENED_SUBSCRIPTION_MODAL, params);
          }}
        />
      )}
    </>
  );
}

const AvatarAndName = ({
  comedian,
}: {
  comedian: Comedian & {
    is_punchup_verified: boolean;
  };
}) => {
  return (
    <>
      <div className="relative shrink-0">
        <ComedianAvatar
          comedianImgUrl={
            comedian.about_img ? getCDNImageURL(comedian.about_img) : undefined
          }
          comedianName={comedian.display_name || ""}
          isNotPunchupComedian={!comedian.is_punchup_verified}
          size="md"
        />
        {comedian.is_punchup_verified && (
          <div className="absolute -top-0.5 -right-0.5">
            <PunchupVerifiedBadge size={18} borderColor="white" withTooltip />
          </div>
        )}
      </div>
      <ComedianName comedianName={comedian.display_name || ""} size="md" />
    </>
  );
};
